import { ROLE_LABELS } from '@whn/shared';
import { LogOut, Menu, Monitor, Moon, Search, Settings, Sun, UserRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/features/auth/auth-context';
import { NotificationsMenu } from '@/features/notifications/notifications-menu';
import { type ThemePreference, useTheme } from '@/features/theme/theme-context';
import { initials } from '@/lib/utils';

export function Topbar({
  onOpenSidebar,
  onOpenSearch,
}: {
  onOpenSidebar: () => void;
  onOpenSearch: () => void;
}) {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();

  if (!user) return null;

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border-subtle bg-surface-raised px-4">
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden"
        onClick={onOpenSidebar}
        aria-label="Open navigation menu"
      >
        <Menu aria-hidden="true" />
      </Button>

      {/*
        A button, not an input. The palette owns the real text field, and two
        inputs — one to click, one to type in — is a control that lies about
        where your keystrokes go.
      */}
      <button
        type="button"
        onClick={onOpenSearch}
        className="flex h-9 max-w-md flex-1 items-center gap-2 rounded-md border border-border-subtle bg-surface-sunken px-3 text-sm text-ink-subtle transition-colors hover:border-border-strong hover:text-ink-muted"
      >
        <Search className="size-4 shrink-0" aria-hidden="true" />
        <span className="flex-1 text-left">Search records…</span>
        <kbd className="hidden shrink-0 rounded border border-border-subtle bg-surface-raised px-1.5 py-0.5 text-[10px] font-medium sm:inline">
          /
        </kbd>
      </button>

      <div className="ml-auto flex items-center gap-1">
        <NotificationsMenu />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-surface-sunken"
              aria-label={`Account menu for ${user.fullName}`}
            >
              <span
                className="flex size-7 shrink-0 items-center justify-center rounded-full bg-brand text-xs font-semibold text-white"
                aria-hidden="true"
              >
                {initials(user.firstName, user.lastName)}
              </span>
              <span className="hidden sm:block">
                <span className="block text-sm font-medium leading-tight text-ink">
                  {user.fullName}
                </span>
                <span className="block text-xs leading-tight text-ink-subtle">
                  {ROLE_LABELS[user.role]}
                </span>
              </span>
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end">
            <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => navigate('/settings/profile')}>
              <UserRound className="size-4" aria-hidden="true" />
              My profile
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => navigate('/settings/organization')}>
              <Settings className="size-4" aria-hidden="true" />
              Organization settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Appearance</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={theme}
              onValueChange={(value) => setTheme(value as ThemePreference)}
            >
              <DropdownMenuRadioItem value="light" onSelect={(event) => event.preventDefault()}>
                <Sun className="size-4" aria-hidden="true" />
                Light
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="dark" onSelect={(event) => event.preventDefault()}>
                <Moon className="size-4" aria-hidden="true" />
                Dark
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="system" onSelect={(event) => event.preventDefault()}>
                <Monitor className="size-4" aria-hidden="true" />
                System
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem destructive onSelect={handleLogout}>
              <LogOut className="size-4" aria-hidden="true" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
